/**
 * The shape of an answer that has not arrived yet.
 *
 * **A skeleton, never a spinner, and never one for the whole tab.** Stats used to replace every
 * screen with the word `Loading…` until its slowest read finished, which meant the eleven panels
 * that were already computed waited on the one that was not — and the reader was shown nothing at
 * all for three seconds (Alex, 8 Aug 2026). A skeleton says *this panel, this shape, shortly*; a
 * spinner says *something is happening somewhere*, which on a twelve-panel screen is not
 * information.
 *
 * Three rules the panels below follow:
 *
 * - **A panel that needs nothing renders immediately.** Its heading, its note, its buttons — all of
 *   that is structure, and structure never waits on data.
 * - **A panel draws its own shape**, not a generic block: rows of bars for a table, a band for a
 *   chart. That is what makes the wait feel like the page arriving rather than the page being
 *   replaced.
 * - **A skeleton is silent about how long.** It carries no percentage and no message. Where there
 *   genuinely is something worth saying — that a figure is cached from last night's job, that a
 *   scan is reading half a million reviews — the panel says it in its own note, beside the heading,
 *   because that is a fact about the number rather than about the wait.
 *
 * The pulse is `--skeleton`, a single token, and it is deliberately slow: at any speed that reads
 * as "activity" it competes with the real content already on screen.
 */
export function Skeleton({
  width = "100%",
  height = 12,
  className = "",
}: {
  /** CSS length. A varied width per row is what stops a stack of these reading as a table. */
  width?: number | string;
  height?: number;
  className?: string;
}) {
  return (
    <span
      aria-hidden
      className={`skeleton block rounded-[var(--r-2)] ${className}`}
      style={{ width, height }}
    />
  );
}

/**
 * A stack of rows, for a panel whose content is a list or a table.
 *
 * `widths` gives the last column's share per row so the block has the texture of real rows rather
 * than of a grey box. Defaults to something table-shaped.
 */
export function SkeletonRows({
  rows = 5,
  height = 12,
  gap = 14,
  widths = ["68%", "82%", "74%", "88%", "62%", "79%", "70%"],
}: {
  rows?: number;
  height?: number;
  gap?: number;
  widths?: string[];
}) {
  return (
    <div className="flex flex-col" style={{ gap }} aria-hidden>
      {Array.from({ length: rows }, (_, index) => (
        <Skeleton key={index} height={height} width={widths[index % widths.length]} />
      ))}
    </div>
  );
}

/**
 * The block a chart leaves behind — one band at the chart's own height.
 *
 * Deliberately **not** a row of fake bars. A bar chart's skeleton drawn as bars is a chart of made-up
 * data, and at a glance that is indistinguishable from the real thing having loaded: the reader has
 * to look twice to find out whether they are reading their collection or a placeholder. One flat
 * band cannot be mistaken for an answer.
 */
export function SkeletonChart({ height = 78 }: { height?: number }) {
  return <Skeleton height={height} className="rounded-[var(--r-2)]" />;
}

/**
 * The label a slow panel wears while it computes — `計算中`, with a dot.
 *
 * Sits in the panel's `right` slot, where a legend would go, so the heading row stays put when the
 * real content arrives. It is the one piece of loading *text* on these screens, and it earns its
 * place by naming the panel that is waiting rather than the screen.
 */
export function ComputingNote({ children = "計算中" }: { children?: string }) {
  return (
    <span className="flex items-center gap-[6px] text-[14px]/[1.4] text-ink-4">
      <span aria-hidden className="skeleton h-[7px] w-[7px] rounded-full" />
      {children}
    </span>
  );
}

/**
 * A headline figure that has not arrived — the block, with its real caption beneath.
 *
 * The caption is **not** skeletoned: it is structure, it is already known, and showing it is what
 * tells the reader which number is coming. A pair of anonymous grey blocks would not.
 */
export function StatSkeleton({ note, width = 96 }: { note: string; width?: number }) {
  return (
    <div className="flex flex-col gap-[6px]">
      <Skeleton width={width} height={26} className="rounded-[var(--r-2)]" />
      <span className="text-[14px]/[1.4] text-ink-4">{note}</span>
    </div>
  );
}
